<template>
  <RevDrawer
    :back-button-label="i18n(translations.back)"
    :close-button-label="i18n(translations.cancel)"
    :has-back-button="shouldShowBackButton"
    :name="MODAL_NAME"
    :title="modalTitle"
    @back="handleBack"
    @close="handleClose"
  >
    <template #body>
      <List
        v-if="selectedListItem === null"
        @select-list-item="handleSelectListItem"
      />

      <div v-if="shouldShowForm">
        <RevInfoBlock
          v-if="apiError"
          class="my-16"
          :icon="IconWarning"
          :title="apiError.title"
          variant="danger"
        >
          {{
            apiError.type === '/errors/validation-error' &&
            apiError.errors?.[0]?.title
              ? apiError.errors?.[0].title
              : apiError.detail
          }}
        </RevInfoBlock>

        <ExportAllOrders
          v-if="selectedListItem === 'all'"
          @submit="handleSubmit"
        />
        <ExportMissingImeiOrders
          v-if="selectedListItem === 'missing-imei-numbers'"
          @submit="handleSubmit"
        />
        <ExportBackShipLabels
          v-if="selectedListItem === 'backship-labels'"
          @submit="handleSubmit"
        />
        <ExportInvoices
          v-if="selectedListItem === 'invoices'"
          @submit="handleSubmit"
        />
        <ExportDeliverySlips
          v-if="selectedListItem === 'delivery-slips'"
          @submit="handleSubmit"
        />
      </div>
    </template>

    <template #footer v-if="shouldShowForm">
      <div class="flex flex-col items-center">
        <RevButton
          class="mb-16"
          data-qa="export-file-button"
          :form="FORM_ID"
          full-width="always"
          :loading="isLoading"
          type="submit"
          variant="primary"
        >
          {{ i18n(translations.submit) }}
        </RevButton>

        <RevLink @click="handleCancel">
          {{ i18n(translations.cancel) }}
        </RevLink>
      </div>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api'
import {
  postMerchantOrdersExport,
  postMerchantOrdersExportAll,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants-orders/merchant-orders-page'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevLink } from '@ds/components/Link'
import { closeModal } from '@ds/components/ModalBase'
import { IconWarning } from '@ds/icons/IconWarning'

import { FORM_ID, MODAL_NAME } from './ExportDrawer.constants'
import translations from './ExportDrawer.translations'
import type { FormValues, ListItem } from './ExportDrawer.types'
import ExportAllOrders from './components/ExportAllOrders/ExportAllOrders.vue'
import ExportBackShipLabels from './components/ExportBackShipLabels/ExportBackShipLabels.vue'
import ExportDeliverySlips from './components/ExportDeliverySlips/ExportDeliverySlips.vue'
import ExportInvoices from './components/ExportInvoices/ExportInvoices.vue'
import ExportMissingImeiOrders from './components/ExportMissingImeiOrders/ExportMissingImeiOrders.vue'
import List from './components/List/List.vue'

const i18n = useI18n()

const { openSuccessToast } = useTheToast()

const apiError = ref<null | HttpApiError>(null)
const isLoading = ref(false)

const selectedListItem = ref<ListItem | null>(null)
const shouldShowForm = computed(() => Boolean(selectedListItem.value))
const shouldShowBackButton = computed(() => Boolean(selectedListItem.value))

const modalTitle = computed(() => {
  const titles: Record<ListItem, string> = {
    all: i18n(translations.exportAll),
    'backship-labels': i18n(translations.exportBackshipLabels),
    'delivery-slips': i18n(translations.exportDeliverySlips),
    invoices: i18n(translations.exportInvoices),
    'missing-imei-numbers': i18n(translations.exportMissingImeiNumbers),
  }

  return selectedListItem.value
    ? titles[selectedListItem.value]
    : i18n(translations.title)
})

function reset() {
  apiError.value = null
  selectedListItem.value = null
}

async function handleSubmit(formValues: FormValues) {
  try {
    isLoading.value = true

    const endpoint =
      selectedListItem.value === 'all'
        ? postMerchantOrdersExportAll
        : postMerchantOrdersExport
    const pathParams =
      selectedListItem.value === 'all'
        ? {}
        : { exportType: selectedListItem.value }

    await $httpFetch(endpoint, {
      pathParams,
      body: removeEmptyValuesInObject(formValues),
    })

    openSuccessToast({
      title: i18n(translations.fileExportSuccessTitle),
      content: i18n(translations.fileExportSuccessDescription),
    })

    reset()
    closeModal(MODAL_NAME)
  } catch (error) {
    apiError.value = HttpApiError.fromAnyError(error as Error)
  } finally {
    isLoading.value = false
  }
}

function handleBack() {
  reset()
}

function handleClose() {
  reset()
}

function handleCancel() {
  reset()
  closeModal(MODAL_NAME)
}

function handleSelectListItem(listItem: ListItem) {
  selectedListItem.value = listItem
}
</script>
